import React, { useEffect, useState, useRef } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Row,
  Col,
  message,
  Typography, Drawer, Divider
} from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { updateForm, saveContest, resetForm, uploadContestImages, updateContest } from "../../store/contestSlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import CustomUploadAdapter from "../../utils/CustomUploadAdapter";
import { API_BASE_URL } from "../../services/apiConstant";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import "./ContestForm.css";
import TimeZone from "../../services/timeZone";

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface ContestData {
  id: string;
  title: string;
  createdAt: string;
  status: number;
  StartDate?: string;
  EndDate?: string;
  description?: string;
  imageCount?: number;
  noOfSubmission?: number;
  submissionCount?: number;
  termsAndCondition?: string;
  contestParticipantViewResponse?: Array<any>;
  contestManagementImageResponses?: Array<{
    id: string;
    contestID: string;
    fileName: string;
    fileUrl: string;
  }>;
}

const ContestForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editorData, setEditorData] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState({ count: 0, size: 0 });
  // const editorInitializedRef = useRef(false);
  const dispatch = useDispatch<AppDispatch>();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false); // ✅ loader state

  const { form: formData, publishLoading, draftLoading, error } =
    useSelector((state: RootState) => state.contest);

  const [mode, setMode] = useState<"view" | "edit" | "view-past" | "create">(
    (location.state as any)?.mode || "create"
  );
  const { contest, contestData } =
    (location.state as { contest?: ContestData; contestData?: ContestData; }) || {};


  // Clear all fields when coming in create mode
  useEffect(() => {
    if (mode === "create") {
      dispatch(resetForm());     // clear redux
      form.resetFields();        // clear antd form
      setEditorData("");         // clear CKEditor
      setUploadStatus({ count: 0, size: 0 }); // reset upload status
      (window as any).pendingContestImages = []; // clear pending images
      (window as any).serverImages = []; // clear server images
    }
  }, [mode, dispatch, form]);

  const extractImages = async (html: string): Promise<string[]> => {
    const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
    const images: string[] = [];
    let match;

    const promises: Promise<void>[] = [];

    while ((match = imgRegex.exec(html))) {
      const src = match[1];

      if (src.startsWith("data:image")) {
        images.push(src);
      } else if (src.startsWith("blob:")) {
        const promise = fetch(src)
          .then(res => res.blob())
          .then(blob => new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              images.push(reader.result as string);
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }));
        promises.push(promise);
      }
    }

    await Promise.all(promises); // wait for all blob conversions
    return images;
  };

  // Function to process description and embed contest images
  const processDescriptionImages = (description: string, contestImages: any[]) => {
    if (!description) {
      return '';
    }

    let processedDescription = description;

    // First, replace any existing blob URLs with actual image URLs
    if (contestImages && contestImages.length > 0) {
      // Dynamic blob URL pattern that works across all environments
      const currentOrigin = window.location.origin;

      // Multiple patterns to catch different blob URL formats
      const blobPatterns = [
        new RegExp(`blob:${currentOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/[a-f0-9-]+`, 'g'),
        /blob:https?:\/\/[^\/]+\/[a-f0-9-]+/g,  // Any https blob URL
        /blob:http:\/\/[^\/]+\/[a-f0-9-]+/g,   // Any http blob URL
        /blob:[a-f0-9-]+/g                     // Simple blob pattern
      ];

      // Find all blob URLs in the description using multiple patterns
      let allBlobMatches: string[] = [];
      blobPatterns.forEach(pattern => {
        const matches = processedDescription.match(pattern);
        if (matches) {
          allBlobMatches = [...allBlobMatches, ...matches];
        }
      });

      // Remove duplicates
      allBlobMatches = [...new Set(allBlobMatches)];

      // Replace blob URLs with actual image URLs
      if (allBlobMatches.length > 0) {
        allBlobMatches.forEach((blobUrl, index) => {
          if (contestImages[index]) {
            processedDescription = processedDescription.replace(blobUrl, contestImages[index].fileUrl);
          }
        });
      }

      // Prefix any non-absolute image srcs with API base URL
      processedDescription = processedDescription.replace(/<img([^>]+)src=["']([^"'>]+)["']([^>]*)>/g, (match, pre, src, post) => {
        if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
          return match;
        }
        const absolute = `${API_BASE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
        return `<img${pre}src="${absolute}"${post}>`;
      });

      // Check if description already has images
      const hasImages = processedDescription.includes('<img') || processedDescription.includes('<figure');

      // If no images in description, append contest images below the text
      if (!hasImages && contestImages.length > 0) {
        console.log('No images found in description, appending contest images below text:', contestImages);

        // Filter out invalid images and create HTML for valid ones
        const validImages = contestImages.filter((image, index) => {
          const isValid = image.fileUrl &&
            image.fileUrl.startsWith('http') &&
            image.fileUrl.length > 10;

          if (!isValid) {
            console.warn(`Skipping invalid image at index ${index}:`, image);
          }

          return isValid;
        });


        // Create image HTML for each valid contest image
        const imageHtml = validImages.map((image, index) => {
          return `<figure class="image image-style-align-left"><img style="aspect-ratio:auto;" src="${image.fileUrl}" width="200" height="200" onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg=='" alt="${image.fileName || 'Contest Image'}"></figure>`;
        }).join('');

        // Append images below the description text with proper spacing (matching wireframe layout)
        processedDescription = processedDescription +
          '<p><br><br>&nbsp;</p>' +
          '<p>&nbsp;</p>' +
          '<p>&nbsp;</p>' +
          '<p>&nbsp;</p>' +
          '<p>&nbsp;</p>' +
          imageHtml;
      }
    }

    return processedDescription;
  };

  // Extract all image src URLs from html
  const extractImageSrcsFromHtml = (html: string): string[] => {
    if (!html) return [];
    const srcs: string[] = [];
    const imgRegex = /<img[^>]+src=["']([^"'>]+)["'][^>]*>/g;
    let match;
    while ((match = imgRegex.exec(html))) {
      srcs.push(match[1]);
    }
    return srcs;
  };

  // Estimate server image size (since we don't have actual file sizes)
  const estimateServerImageSize = (imageUrl: string): number => {
    return 2 * 1024 * 1024; // 2MB per server image as estimate (more realistic)
  };


  // Populate form with contest data when editing
  useEffect(() => {
    if (contestData) {
      // Process description to replace blob URLs with actual image URLs
      let processedDescription = processDescriptionImages(
        contestData.description || '',
        contestData.contestManagementImageResponses || []
      );

      // ✅ Append missing server images (if description has fewer images than backend)
      try {
        const existingUrls = Array.from(
          processedDescription.matchAll(/<img[^>]+src="([^"]+)"/g)
        ).map((m) => m[1]);

        (contestData.contestManagementImageResponses || []).forEach((img: any) => {
          if (img?.fileUrl && !existingUrls.includes(img.fileUrl)) {
            processedDescription += `<figure class="image"><img src="${img.fileUrl}" style="max-width:100%;height:auto;" /></figure>`;
          }
        });
      } catch (err) {
        console.warn("Error appending missing images:", err);
      }

      // Store server images for tracking
      const serverImages = (contestData.contestManagementImageResponses || []).map((img: any) => ({
        ...img,
        fileSize: estimateServerImageSize(img.fileUrl || '') // Estimate size for server images
      }));
      (window as any).serverImages = serverImages;

      // Helper function to parse date from "Wed, 17-09-2025" format and convert to MM-DD-YYYY
      const parseContestDate = (dateStr: string) => {
        if (!dateStr) return '';
        // Extract date part from "Wed, 17-09-2025" format
        const dateMatch = dateStr.match(/(\d{2}-\d{2}-\d{4})/);
        if (dateMatch) {
          const [day, month, year] = dateMatch[1].split('-');
          return `${month}-${day}-${year}`; // Convert to MM-DD-YYYY format
        }
        return '';
      };

      // Map contest data to form fields
      const formValues = {
        title: contestData.title || '',
        startDate: parseContestDate(contestData.StartDate || ''),
        endDate: parseContestDate(contestData.EndDate || ''),
        description: processedDescription,
        termsAndCondition: contestData.termsAndCondition || '',
        imageCount: contestData.imageCount ?? 1,
        contestImagesID: (contestData.contestManagementImageResponses || []).map((img) => img.id).filter(Boolean),
      };

      // Update Redux store
      dispatch(updateForm(formValues));

      // Update form fields
      form.setFieldsValue(formValues);

      setEditorData(processedDescription || "");

      // Initialize upload status with server images
      const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
      let match;
      let count = 0;

      while ((match = imgRegex.exec(processedDescription))) {
        count++;
      }

      // Calculate estimated size for server images
      const estimatedSize = serverImages.reduce((total: number, img: any) => total + img.fileSize, 0);

      setUploadStatus({ count, size: estimatedSize });

    }
  }, [location.state, dispatch, form]);

  const saveContestWithStatus = async (status: number) => {
    try {
      setLoading(true);

      const values = form.getFieldsValue();
      const start = values.startDate;

      //  Validate start date
      if (start && dayjs(start).isBefore(dayjs())) {
        messageApi.error("Start date & time cannot be earlier than the current time!");
        return;
      }

      const isDraft = status === 0;
      const passed =
        (location.state as { contest?: ContestData; contestData?: ContestData })?.contest ||
        (location.state as { contest?: ContestData; contestData?: ContestData })?.contestData;

      //  Basic field validation for Draft / Full validation for Publish
      if (isDraft) {
        try {
          await form.validateFields(["title"]);
        } catch {
          messageApi.error("Please enter Contest Name before saving as draft.");
          return;
        }
      } else {
        await form.validateFields();

        // Validate duration
        if (!formData.startDate || !formData.endDate) {
          messageApi.error("Please select contest duration!");
          return;
        }

        // Validate description
        const plainDescription = (editorData || "").replace(/<[^>]*>/g, "").trim();
        if (!plainDescription) {
          messageApi.error("Please enter contest description!");
          return;
        }
      }

      //  Prepare common payload
      const basePayload = {
        title: formData.title || values.title,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        imageCount: formData.imageCount || 1,
        description: editorData || formData.description || null,
        termsAndCondition: formData.termsAndCondition || null,
        status,
        contestImagesID: Array.isArray((formData as any).contestImagesID)
          ? (formData as any).contestImagesID
          : [],
        timezone: TimeZone.getFriendlyTimeZone(),
      };

      let contestRes;
      const token = getToken();
      if (!token) throw new Error("No token found");

      //  EDIT MODE
      if (passed?.id) {
        const currentSrcs = extractImageSrcsFromHtml(editorData || basePayload.description);
        const originalImages =
          (passed.contestManagementImageResponses || []) as Array<{ id: string; fileUrl: string }>;
        const idsFromDescription = originalImages
          .filter((img) => img.fileUrl && currentSrcs.some((src) => src.includes(img.fileUrl)))
          .map((img) => img.id);

        const updateData = {
          ...basePayload,
          contestImagesID: idsFromDescription,
        };

        try {
          contestRes = await dispatch(updateContest({ id: passed.id, data: updateData })).unwrap();

          const decryptedData = await decryptMessage(contestRes.Data, token);
          const contestId = decryptedData.id;

          //  Upload new images if any
          const images = await extractImages(editorData || "");
          if (images.length > 0 && contestId) {
            await dispatch(uploadContestImages({ contestId, images }));
          }

          messageApi.success(isDraft ? "Contest saved as draft!" : "Contest updated successfully!");
        } catch (err: any) {
          messageApi.error(err);
          return;
        }
      }
      //  CREATE MODE
      else {
        try {
          contestRes = await dispatch(saveContest({ data: basePayload, status })).unwrap();

          const decryptedData = await decryptMessage(contestRes.Data, token);
          const contestId = decryptedData.id;

          //  Upload new images if any
          const images = await extractImages(editorData || "");
          if (images.length > 0 && contestId) {
            await dispatch(uploadContestImages({ contestId, images }));
          }

          messageApi.success(
            isDraft ? "Contest saved as draft successfully!" : "Contest created successfully!"
          );
        } catch (err: any) {
          messageApi.error(err);
          return;
        }
      }

      //  Reset after successful save
      dispatch(resetForm());
      form.resetFields();
      setEditorData("");
      setTimeout(() => navigate(-1), 1000);
    } catch (err: any) {
      console.error("Save error:", err);
      const fallbackMsg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        "Unexpected error while saving contest.";
      messageApi.error(fallbackMsg);
    } finally {
      setLoading(false);
    }
  };


  const pageTitle =
    !contest && !contestData
      ? "Add Contest Section"
      : mode === "view"
        ? "View Contest Section"
        : mode === "view-past"
          ? "Past Contest Section"
          : "Edit Contest Section";


  return (
    <>
      <div style={{ position: "relative" }}>
        {/*  Loader Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2000,
            }}
          >
            <img
              src="/images/newLoading.gif" // <-- path to your GIF
              alt="Loading..."
              style={{ width: 200, height: 150 }}
            />
          </div>
        )}
      </div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={3} style={{ margin: 0, color: '#41787C' }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined style={{ fontSize: 18, fontWeight: "bold", color: "#41787C", marginRight: 10 }} />}
              onClick={() => navigate(-1)}
              style={{ marginBottom: "10px", padding: 0 }}
            />
            {pageTitle}
          </Typography.Title>
        </Col>
        {mode === "view" && (
          <Col>
            <Button
              type="primary"
              className="submit-btn btn-publish"
              icon={<EditOutlined style={{ fontSize: 16 }} />}
              onClick={() => setMode("edit")}
            >
              Edit
            </Button>
          </Col>
        )}
      </Row>

      {contextHolder}
      <Form form={form} layout="vertical" requiredMark={false}
        style={{
          padding: "16px",
          maxWidth: "100%",
        }}>
        <Form.Item
          label={
            <span>
              <span style={styles.contestlabel}>Contest Name</span>
              <span style={{ color: 'red', marginLeft: 4, fontSize: '16px' }}>*</span>
            </span>
          }
          name="title"

          rules={[
            { required: true, message: "Please enter contest name!" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                const trimmed = value.trim();

                //  Block if value is purely numeric or decimal (e.g. "123", "12.5", ".5")
                if (/^[0-9.]+$/.test(trimmed)) {
                  return Promise.reject(
                    "Contest name cannot contain only numbers or decimals!"
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            value={formData.title}
            disabled={mode === "view" || mode === "view-past"}
            onChange={(e) => dispatch(updateForm({ title: e.target.value }))}
            placeholder="Enter contest name"
          />
        </Form.Item>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12}>
            <Form.Item

              label={
                <span>
                  <span style={styles.contestlabel}> Contest Duration</span>
                  <span style={{ color: 'red', marginLeft: 4, fontSize: '16px' }}>*</span>
                </span>
              }
              rules={[
                {
                  validator: () => {
                    if (!formData.startDate || !formData.endDate) {
                      return Promise.reject(new Error("Please select contest duration!"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <RangePicker
                style={{ width: "100%", height: "43px" }}
                format="MM/DD/YYYY"
                disabled={mode === "view" || mode === "view-past"}
                disabledDate={(current) => current && current < dayjs().startOf("day")}
                value={
                  formData.startDate && formData.endDate
                    ? [dayjs(formData.startDate), dayjs(formData.endDate)]
                    : undefined
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    if (dates[1].isBefore(dates[0])) {
                      messageApi.error("End date must be after start date!");
                      return;
                    }
                    dispatch(
                      updateForm({
                        startDate: dates[0].format("YYYY-MM-DD"),
                        endDate: dates[1].format("YYYY-MM-DD"),
                      })
                    );
                  } else {
                    dispatch(updateForm({ startDate: "", endDate: "" }));
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <span>
                  <span style={styles.contestlabel}> No. of submissions allowed</span>
                  <span style={{ color: 'red', marginLeft: 4, fontSize: '16px' }}>*</span>
                </span>
              }
              name="imageCount"
              rules={[{ required: true, message: "Please enter number of submission!" }]}
            >
              <Input
                value={formData.imageCount}
                disabled={mode === "view" || mode === "view-past"}
                type="number"
                min={1}
                step={1} //  disables decimal arrows on most browsers
                onKeyDown={(e) => {
                  //  Block dot, comma, minus, and 'e' (scientific notation)
                  if ([".", ",", "-", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;

                  //  Prevent decimals from being processed programmatically
                  if (value.includes(".") || value.includes(",")) return;

                  const intValue = parseInt(value, 10);
                  if (isNaN(intValue) || intValue < 1) {
                    messageApi.warning("No. of submissions must be at least 1");
                    return;
                  }

                  dispatch(updateForm({ imageCount: intValue }));
                }}
                placeholder="Enter number of submissions"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={
            <span>
              <span style={styles.contestlabel}> Description</span>
              <span style={{ color: 'red', marginLeft: 4, fontSize: '16px' }}>*</span>
            </span>
          }
          name="description"
          rules={[
            {
              validator: (_, value) => {
                const plainText = value
                  ? value.replace(/<[^>]*>/g, "").trim() // remove HTML tags
                  : "";

                // Required check
                if (!plainText) {
                  return Promise.reject("Please enter contest description!");
                }

                // Block if it's only numbers or decimals (e.g. "123", "12.5")
                if (/^[0-9.]+$/.test(plainText)) {
                  return Promise.reject(
                    "Description cannot contain only numbers or decimals!"
                  );
                }

                // Max length check (e.g. 500 characters)
                if (plainText.length > 2000) {
                  return Promise.reject(
                    "Description cannot exceed 2000 characters."
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <CKEditor
            editor={ClassicEditor as any}
            data={editorData}
            disabled={mode === "view" || mode === "view-past"}
            config={{
              placeholder: "Enter contest description",
              toolbar: [
                "heading", "|",
                "bold", "italic", "link", "bulletedList", "numberedList", "|",
                "blockQuote", "undo", "redo", "imageUpload",
                "imageStyle:alignLeft", "imageStyle:alignCenter", "imageStyle:alignRight", "imageStyle:full", "|",
                "alignment",
              ],
              image: {
                toolbar: [
                  "imageStyle:alignLeft",
                  "imageStyle:alignCenter",
                  "imageStyle:alignRight",
                  "imageStyle:full"
                ],
                resizeUnit: "%",
                resizeOptions: [
                  { name: "resizeImage:original", label: "Original", value: null },
                  { name: "resizeImage:25", label: "25%", value: "25" },
                  { name: "resizeImage:50", label: "50%", value: "50" },
                  { name: "resizeImage:75", label: "75%", value: "75" }
                ]
              },
            }}
            onReady={(editor) => {
              editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
                const adapter = new CustomUploadAdapter(loader);

                // Wrap the upload method to catch errors
                const originalUpload = adapter.upload.bind(adapter);
                adapter.upload = async () => {
                  try {
                    return await originalUpload();
                  } catch (error: any) {
                    messageApi.error(error.message || 'Upload failed');
                    throw error;
                  }
                };

                return adapter;
              };

              // Initialize current editor content
              (window as any).currentEditorContent = editor.getData();

              // Handle upload errors and update status
              editor.model.document.on('change:data', () => {
                const data = editor.getData();
                (window as any).currentEditorContent = data;

                // Update status based on actual images in editor
                const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
                let match;
                let count = 0;

                while ((match = imgRegex.exec(data))) {
                  count++;
                }

                // For size calculation, we'll use both pending and server images
                const pendingImages = (window as any).pendingContestImages || [];
                const serverImages = (window as any).serverImages || [];
                const editorImageSrcs: string[] = [];
                const imgRegex2 = /<img[^>]+src=['"]([^'">]+)['"]/g;
                let match2;

                while ((match2 = imgRegex2.exec(data))) {
                  editorImageSrcs.push(match2[1]);
                }

                const activePendingImages = pendingImages.filter((file: File) => {
                  const blobUrl = URL.createObjectURL(file);
                  const blobId = blobUrl.split('/').pop();
                  return editorImageSrcs.some(src => src.includes(blobId || ''));
                });

                const activeServerImages = serverImages.filter((image: any) => {
                  const imageUrl = image.fileUrl || '';
                  return editorImageSrcs.some(src => {
                    // More flexible matching - check if the image URL is contained in the src
                    return src.includes(imageUrl) || imageUrl.includes(src);
                  });
                });

                const pendingSize = activePendingImages.reduce((total: number, file: File) => total + file.size, 0);
                const serverSize = activeServerImages.reduce((total: number, image: any) => total + (image.fileSize || 0), 0);

                // Apply fallback size calculation
                let currentSize = pendingSize + serverSize;
                if (count > 0) {
                  const estimatedSize = count * 2 * 1024 * 1024; // 2MB per image
                  if (currentSize === 0) {
                    currentSize = estimatedSize;
                    // console.log('onReady: Applied fallback size calculation:', currentSize, 'bytes for', count, 'images');
                  } else if (currentSize < estimatedSize * 0.5) {
                    currentSize = estimatedSize;
                    // console.log('onReady: Applied fallback due to low calculated size:', currentSize, 'bytes for', count, 'images');
                  }
                }

                setUploadStatus({ count, size: currentSize });
              });
            }}
            onChange={(_, editor) => {
              const data = editor.getData() || "";
              setEditorData(data);

              // Update Ant Design form field
              form.setFieldsValue({ description: data });

              // Optional: validate as user types
              form.validateFields(["description"]);

              // Store current editor content for image tracking
              (window as any).currentEditorContent = data;

              // Update upload status based on actual images in editor
              const updateUploadStatus = () => {
                const pendingImages = (window as any).pendingContestImages || [];
                const serverImages = (window as any).serverImages || [];

                // Extract image sources from current editor content
                const imgRegex = /<img[^>]+src=['"]([^'">]+)['"]/g;
                const editorImageSrcs: string[] = [];
                let match;

                while ((match = imgRegex.exec(data))) {
                  editorImageSrcs.push(match[1]);
                }

                // Filter pending images to only include those still in the editor
                const activePendingImages = pendingImages.filter((file: File) => {
                  const blobUrl = URL.createObjectURL(file);
                  const blobId = blobUrl.split('/').pop();
                  return editorImageSrcs.some(src => src.includes(blobId || ''));
                });

                // Filter server images to only include those still in the editor
                const activeServerImages = serverImages.filter((image: any) => {
                  const imageUrl = image.fileUrl || '';
                  return editorImageSrcs.some(src => {
                    // More flexible matching - check if the image URL is contained in the src
                    return src.includes(imageUrl) || imageUrl.includes(src);
                  });
                });

                const currentCount = activePendingImages.length + activeServerImages.length;
                const pendingSize = activePendingImages.reduce((total: number, file: File) => total + file.size, 0);
                const serverSize = activeServerImages.reduce((total: number, image: any) => total + (image.fileSize || 0), 0);

                // Fallback: if server images aren't matching, estimate based on total server images
                let fallbackServerSize = 0;
                if (serverImages.length > 0 && activeServerImages.length === 0) {
                  // If we have server images but none are matching, use all server images
                  fallbackServerSize = serverImages.reduce((total: number, image: any) => total + (image.fileSize || 0), 0);
                }

                // Final fallback: if still no size, estimate based on image count
                let finalSize = pendingSize + serverSize + fallbackServerSize;

                // Always apply fallback if we have images but no size calculation
                if (currentCount > 0) {
                  const estimatedSize = currentCount * 2 * 1024 * 1024; // 2MB per image
                  if (finalSize === 0) {
                    finalSize = estimatedSize;
                    // console.log('Applied fallback size calculation:', finalSize, 'bytes for', currentCount, 'images');
                  } else {
                    // If we have some size but it seems too low, use the higher of the two
                    if (finalSize < estimatedSize * 0.5) { // If calculated size is less than half of estimated
                      finalSize = estimatedSize;
                      //  console.log('Applied fallback due to low calculated size:', finalSize, 'bytes for', currentCount, 'images');
                    }
                  }
                }

                const currentSize = finalSize;

                setUploadStatus({ count: currentCount, size: currentSize });
              };

              updateUploadStatus();

              // reflect in redux too for preview
              dispatch(updateForm({ description: data }));
              form.validateFields(["description"]);
            }}
          />

          <style>
            {`
                    .ck-editor__editable_inline {
                      min-height: 200px;
                      max-height: 400px;
                      overflow-y: auto;
                       background-color: ${mode === "view" ? "#00000040" : "#00000040"}; /* change bg if disabled */
                       color: ${mode === "view" ? "#6b6969ff" : "#000"}; /* change text color if disabled */
                    }

                    .ck-content img {
                      width: 200px !important;
                      height: 200px !important;
                      object-fit: contain;
                      display: block;
                      margin: 10px auto !important;
                    }

                    /* ✅ Allow scrolling but prevent editing */
                    ${mode === "view" ? `
                      .ck-toolbar {
                        display: none !important;
                      }
                      .ck-editor__editable {
                        pointer-events: auto !important; /* enable scrolling */
                        user-select: text !important; /* allow text selection */
                        cursor: default !important;
                      }
                      .ck-editor__editable[contenteditable="true"] {
                        pointer-events: auto !important;
                        user-select: text !important;
                      }
                      .ck-editor__editable_inline[contenteditable="true"] {
                        pointer-events: auto !important;
                      }
                      .ck-blurred[contenteditable="true"],
                      .ck-focused[contenteditable="true"] {
                        pointer-events: auto !important;
                      }
                      .ck-editor__editable[contenteditable="true"] * {
                        pointer-events: auto !important;
                      }
                      .ck-editor__editable_inline {
                        overflow-y: auto !important;
                      }
                    ` : ""}

                    .ck-powered-by,
                    .ck-powered-by-ckeditor,
                    [class*="ck-powered-by"],
                    .ck-editor__main .ck-powered-by {
                      display: none !important;
                    }
                  `}
          </style>

        </Form.Item>

        <Form.Item
          label={
            <span>
              <span style={styles.contestlabel}> Add Terms & Conditions</span>
              <span style={{ color: 'red', marginLeft: 4, fontSize: '16px' }}>*</span>
            </span>
          }
          name="termsAndCondition"
          rules={[
            {
              validator: (_, value) => {
                const trimmed = value?.trim() || "";

                // Required check
                if (!trimmed) {
                  return Promise.reject("Please enter terms!");
                }

                // Block if only numbers or decimals
                if (/^[0-9.]+$/.test(trimmed)) {
                  return Promise.reject(
                    "Terms cannot contain only numbers or decimals!"
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.TextArea
            value={formData.termsAndCondition}
            disabled={mode === "view" || mode === "view-past"}
            onChange={(e) => dispatch(updateForm({ termsAndCondition: e.target.value }))}
            rows={9}
            placeholder="Enter contest terms & conditions"
          />
        </Form.Item>
        <div className="button-container">
          <Row justify="end" gutter={[16, 16]}>
            <Button
              onClick={() => {
                // Ensure latest CKEditor content is saved to formData before preview
                form.setFieldsValue({ description: editorData });
                dispatch(updateForm({ description: editorData }));
                setPreviewVisible(true);
              }}
              className="submit-btn"
              style={{ marginRight: 12 }}
            >
              Mobile Preview
            </Button>

            {mode !== "view" && (
              <>
                {mode !== "view-past" && (
                  //  SAVE AS DRAFT - Only validates Contest Name
                  <Button
                    type="dashed"
                    loading={draftLoading}
                    onClick={() => saveContestWithStatus(0)}
                    className="submit-btn draft-btn"
                    style={{ marginRight: 12 }}
                  >
                    Save as Draft
                  </Button>
                )}

                {mode !== "view-past" && (
                  //  PUBLISH - Validates ALL fields
                  <Button
                    type="primary"
                    loading={publishLoading}
                    onClick={async () => {
                      try {
                        //  Trigger form validation for ALL fields
                        await form.validateFields();

                        //  If all fields are valid → proceed with publish
                        saveContestWithStatus(1);
                      } catch (error) {
                        // Validation failed → show error message
                        messageApi.error("Please fill all required fields before publishing.");
                      }
                    }}
                    className="submit-btn btn-publish"
                  >
                    {((location.state as { contest?: ContestData; contestData?: ContestData })?.contest ||
                      (location.state as { contest?: ContestData; contestData?: ContestData })?.contestData)
                      ? 'Update Contest'
                      : 'Publish'}
                  </Button>
                )}
              </>
            )}
          </Row>
        </div>
      </Form>

      <Drawer
        placement="right"
        width={window.innerWidth < 768 ? "100%" : 420}
        closable={false}
        headerStyle={{ display: "none" }}
        onClose={() => setPreviewVisible(false)}
        open={previewVisible}
        bodyStyle={{
          padding: 5,
          background: "#f5f5f5",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "320px",
            height: "640px",
            margin: "20px auto",
            border: "12px solid #000",
            borderRadius: "40px",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Top notch */}
          <div
            style={{
              width: "160px",
              height: "22px",
              background: "#000",
              borderRadius: "0 0 20px 20px",
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          />

          {/* Contest content */}
          <div
            style={{
              flex: 1,
              padding: "20px 16px",
              overflowY: "auto",
            }}
          >
            {/* Title */}
            <h3 style={{ textAlign: "center", marginBottom: "12px", marginTop: "12px", color: "#a0522d" }}>
              Contest Section
            </h3>

            {/* Contest name */}
            <h5 style={{ textAlign: "left", marginBottom: "-16px", fontSize: "13px" }}>
              {formData.title || "Contest Title"}
            </h5>
            <Divider style={{ borderColor: "#a0522d", borderWidth: 1 }} />
            {/* Dates */}
            <p style={{ fontSize: "13px", marginBottom: "14px", marginTop: "-13px", color: "#a0522d" }}>
              <strong>Contest Start Date & End Date</strong>
              <br />
              {formData.startDate && formData.endDate
                ? `${dayjs(formData.startDate).format("MM-DD-YYYY")} → ${dayjs(formData.endDate).format("MM-DD-YYYY")}`
                : "No duration selected"}
            </p>

            <h4 style={{ marginTop: "10px", marginBottom: "6px", color: "#a0522d" }}>
              Description
            </h4>
            {/* Description */}
            <div
              className="description-preview"
              dangerouslySetInnerHTML={{
                __html: formData.description || "<p>No description provided</p>",
              }}
              style={{
                fontSize: "12px",
                lineHeight: 1.5,
                marginBottom: "16px",
                overflowWrap: "break-word",
              }}
            />

            <style>
              {`
    .description-preview img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 8px 0;
      display: block;
    }
  `}
            </style>

            {/* Terms */}
            <h4 style={{ marginTop: "10px", marginBottom: "6px", color: "#a0522d" }}>
              Terms & Condition
            </h4>
            <p style={{ fontSize: "12px", whiteSpace: "pre-line", lineHeight: 1.5, }}>
              {formData.termsAndCondition || "No terms provided"}
            </p>
          </div>

          {/* Footer buttons */}
          <div
            style={{
              padding: "12px",
              borderTop: "1px solid #ddd",
              background: "#fafafa",
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <Button onClick={() => setPreviewVisible(false)}>Close Preview</Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ContestForm;

const baseGreen = '#3E767A';
const styles: { [key: string]: React.CSSProperties } = {
  contestlabel: {
    color: '#3E767A',
    fontSize: '18px',
    marginBottom: '7px',
    fontWeight: 600,
    //textAlign: 'left',
    lineHeight: '24px',
  },
};


